// src/components/listing-leads/main-listing-leads.jsx
// Listing (seller) pipeline — the "blend" view (funnel strip + needs-attention
// band + stage-grouped rows) shared with the Buyer pipeline via
// PipelineBlendView. Backed by the listing_leads collection and seller stages.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Plus, Search, X } from "lucide-react";
import PageHeader from "../PageHeader";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { actionType } from "../../context/reducer";
import {
  listenListingLeads,
  updateListingLeadStage,
  updateListingLead,
  updateDealById,
  saveListingLead,
  saveBuyerLead,
  getContactsByIds,
} from "../../utils/firebasefunctions";
import { normalizeContact } from "../../utils/contactNormalize";
import { LISTING_STAGES, STAGE_COLORS, GRADUATION_STAGE, PARKING_STAGES } from "../../utils/listingLeadStages";
import { excludeTestAgents, isTestAgent } from "../../utils/testAgents";
import { isOnLead } from "../../utils/leadOwnership";
import NewListingLeadModal from "./NewListingLeadModal";
import ListingLeadDrawer from "./ListingLeadDrawer";
import PipelineBlendView from "../pipeline/PipelineBlendView";
import { usePersistentAgentFilter } from "../../hooks/usePersistentAgentFilter";

// Coerce a possibly-object field (deal.leadsource / agentName are sometimes
// objects) into a plain display string for the lead card.
const flatName = (v) =>
  v && typeof v === "object" && !Array.isArray(v)
    ? v.name || [v.firstname, v.lastname].filter(Boolean).join(" ") || ""
    : v || "";

// Deals Board stages that are really pipeline work — moved into the pipeline
// and retired from the board. Sellers use the mapped stage; buyers fall back
// to New Lead (their pipeline has no listing-appointment stage).
const STAGE_FROM_BOARD = {
  Prospect: "New Lead",
  "Listing Appointment": "Listing Appointment",
};

export default function MainListingLeads() {
  const [{ user, listingLeads, employees, deals }, dispatch] = useStateValue();
  const [showLost, setShowLost] = useState(false);
  const [showWon, setShowWon] = useState(false);
  const [query, setQuery] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  // "Viewing: <agent>" filter — shared hook: defaults to My Leads, persists per
  // user. Key (listingViewAgent:<uid>) is unchanged so prior picks carry over.
  const [viewAgent, setViewAgent] = usePersistentAgentFilter(user?.id, "listing");

  useEffect(() => {
    const subscribe = () =>
      listenListingLeads((rows) => {
        dispatch({ type: actionType.STORE_LISTING_LEADS, payload: rows });
      });
    let unsub = subscribe();
    // Re-sync whenever the tab regains focus, so a change made elsewhere (e.g.
    // being removed as a co-agent) takes effect immediately when the agent
    // returns to the board — no manual page refresh needed.
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        unsub && unsub();
        unsub = subscribe();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      unsub && unsub();
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [dispatch]);

  const leads = useMemo(() => listingLeads || [], [listingLeads]);

  // ONE-TIME MOVE — pull pre-signing Prospect deals off the Deals Board into
  // the pipelines as New Lead cards (sellers → Listing Pipeline, buyers →
  // Buyers Pipeline). The original deal is soft-deleted (full data preserved)
  // and tagged with the new lead's id; "Sign the Listing" / "Offer Accepted"
  // restores that original deal intact, so this round-trips losslessly and is
  // reversible. Admin-only, guarded (won't re-run on a tagged deal).
  const movedRef = useRef(false);
  useEffect(() => {
    if (movedRef.current) return;
    if (user?.role !== "Admin") return;
    const toMove = (deals || []).filter(
      (d) =>
        STAGE_FROM_BOARD[d?.dealStage] &&
        !(Number(d?.deletedAt) > 0) &&
        !d?.migratedToLeadId &&
        // Request intake deals already have a pipeline lead (created at submit),
        // so don't move them again — that would duplicate the lead. The
        // retired-column cleanup below soft-hides them instead.
        d?.requestType !== "fileRequest",
    );
    if (!toMove.length) return;
    movedRef.current = true;
    (async () => {
      let moved = 0;
      for (const d of toMove) {
        try {
          // Map ALL the deal's clients: first becomes the lead's primary,
          // the rest become co-clients (so nobody is lost moving board→pipeline).
          let contactName = "";
          let coClients = [];
          const ids = (d.customers || []).filter(Boolean);
          if (ids.length) {
            const raw = await getContactsByIds(ids);
            const names = new Map(
              (raw || [])
                .map((c) => normalizeContact(c))
                .filter(Boolean)
                .map((c) => [c.id, c.name || ""]),
            );
            contactName = names.get(ids[0]) || "";
            coClients = ids.slice(1).map((cid) => ({
              contactId: cid,
              contactName: names.get(cid) || "",
            }));
          }
          const isBuyer = d.dealtype === "Buyer";
          // Buyers have no listing-appointment stage — fall back to New Lead.
          const stage = isBuyer ? "New Lead" : STAGE_FROM_BOARD[d.dealStage];
          const common = {
            contactName: contactName || d.clientLastName || "",
            contactId: ids[0] || "",
            coClients,
            source: flatName(d.leadsource),
            stage,
            notes: d.notes || "",
            assignedAgent: d.agentData || "",
            assignedAgentName: flatName(d.agentName),
            // The lead belongs to the deal's AGENT, not the admin running the
            // move — otherwise "who created it" attribution lands on the admin.
            createdby: d.agentData || d.createdby || user?.id || "",
            sourceDealId: String(d.createdat || ""),
          };
          const res = isBuyer
            ? await saveBuyerLead(common)
            : await saveListingLead({
                  ...common,
                  propertyAddress: d.propertyaddress || "",
                  estValue: d.suggestedPrice || d.offerPrice || "",
                  listPrice: d.listPrice || "",
                });
          if (res?.success) {
            await updateDealById(String(d.createdat), {
              deletedAt: Date.now(),
              migratedToLeadId: res.id,
            });
            moved++;
          }
        } catch (e) {
          console.error("Pipeline move failed for", d?.createdat, e);
        }
      }
      if (moved) {
        toast.success(`Moved ${moved} into the pipeline`);
      }
    })();
  }, [deals, user?.id]);

  // One-time repair: an earlier move stamped createdby = the admin who ran it
  // instead of the deal's agent, so a moved lead assigned to another agent
  // (e.g. Joe's "Heather Johnson") wrongly attributed to the admin. Realign
  // createdby to the assigned agent. Only touches MOVED leads (sourceDealId);
  // normally-created leads keep their real creator. Admin-only, idempotent.
  const ownerFixRef = useRef(false);
  useEffect(() => {
    if (ownerFixRef.current) return;
    if (user?.role !== "Admin") return;
    const dirty = (leads || []).filter(
      (l) => l.sourceDealId && l.assignedAgent && l.createdby !== l.assignedAgent,
    );
    if (!dirty.length) return;
    ownerFixRef.current = true;
    dirty.forEach((l) => {
      updateListingLead(l.id, { createdby: l.assignedAgent }).catch((e) =>
        console.error("Lead owner repair failed for", l.id, e),
      );
    });
  }, [leads, user?.id]);

  // One-time name backfill — older listing leads (especially those created from
  // a Listing/Buyer Request) saved only the client's last name, or nothing, in
  // contactName, so the card reads "(unnamed lead)". The link to the real
  // contact (contactId) survived, so resolve the full name from there and patch
  // any lead whose name is blank. Idempotent; admin/supervisor only.
  const nameFixRef = useRef(false);
  useEffect(() => {
    if (nameFixRef.current) return;
    if (user?.role !== "Admin" && user?.role !== "Supervisor") return;
    const missing = (leads || []).filter(
      (l) => l && l.contactId && !String(l.contactName || "").trim(),
    );
    if (!missing.length) return;
    nameFixRef.current = true;
    (async () => {
      try {
        const ids = [...new Set(missing.map((l) => l.contactId))];
        const rows = await getContactsByIds(ids);
        const nameById = {};
        (rows || []).filter(Boolean).forEach((raw) => {
          const name =
            normalizeContact(raw)?.name ||
            raw.name ||
            [raw.firstname, raw.lastname].filter(Boolean).join(" ") ||
            "";
          if (raw.id) nameById[raw.id] = String(name).trim();
        });
        for (const l of missing) {
          const name = nameById[l.contactId];
          if (name) {
            await updateListingLead(
              l.id,
              { contactName: name },
              { skipFubPush: true },
            );
          }
        }
      } catch (e) {
        console.error("Listing-lead name backfill failed:", e);
      }
    })();
  }, [leads, user?.id]);

  // One-time cleanup — soft-hide leftover Humza "Templates"-stage junk deals
  // (not real deals; task templates live in a separate `tasktemplate`
  // collection). Reversible (soft delete), admin-only, idempotent.
  const junkRef = useRef(false);
  useEffect(() => {
    if (junkRef.current) return;
    if (user?.role !== "Admin") return;
    const junk = (deals || []).filter(
      (d) =>
        d?.dealStage === "Templates" &&
        !(Number(d?.deletedAt) > 0) &&
        (isTestAgent({ id: d.agentData }) ||
          isTestAgent({ id: d.createdby }) ||
          isTestAgent({ name: d.agentName })),
    );
    if (!junk.length) return;
    junkRef.current = true;
    junk.forEach((d) => {
      updateDealById(String(d.createdat), { deletedAt: Date.now() }).catch((e) =>
        console.error("Junk-template cleanup failed for", d?.createdat, e),
      );
    });
  }, [deals, user?.id]);

  // One-time cleanup — the Prospect / Listing Appointment board columns are
  // retired (pre-signing work lives in the pipeline now). Soft-hide the leftover
  // Listing/Buyer Request intake deals still parked there (requestType
  // "fileRequest"): each already has a pipeline lead and round-trips to
  // Pre-Market on signing. Anything else in those stages is LEFT UNTOUCHED and
  // flagged in the console for manual re-staging. Reversible, admin-only.
  const retiredColRef = useRef(false);
  useEffect(() => {
    if (retiredColRef.current) return;
    if (user?.role !== "Admin") return;
    const stranded = (deals || []).filter(
      (d) =>
        ["Prospect", "Listing Appointment"].includes(d?.dealStage) &&
        !(Number(d?.deletedAt) > 0),
    );
    if (!stranded.length) return;
    retiredColRef.current = true;
    const requestDeals = stranded.filter((d) => d?.requestType === "fileRequest");
    const others = stranded.filter((d) => d?.requestType !== "fileRequest");
    if (others.length) {
      console.warn(
        `[retired-column cleanup] ${others.length} non-request deal(s) still in Prospect/Listing Appointment — left in place; re-stage manually:`,
        others.map((d) => ({
          id: d.createdat,
          stage: d.dealStage,
          client: d.clientLastName,
          agent: flatName(d.agentName),
        })),
      );
    }
    requestDeals.forEach((d) => {
      updateDealById(String(d.createdat), { deletedAt: Date.now() }).catch((e) =>
        console.error("Retired-column cleanup failed for", d?.createdat, e),
      );
    });
  }, [deals, user?.id]);

  // One-time migration — the "CMA" and "Appt Held" stages were removed
  // (2026-07-15). A lead stranded in a deleted stage silently drops out of
  // every funnel column and stage row, so re-stage them: CMA → Listing
  // Appointment, Appt Held → Appt Set (Henry's mapping). Reversible, admin-only,
  // idempotent (after the move no lead matches). skipFubPush avoids sync churn.
  const RETIRED_STAGE_MAP = { CMA: "Listing Appointment", "Appt Held": "Appt Set" };
  const stageMigrateRef = useRef(false);
  useEffect(() => {
    if (stageMigrateRef.current) return;
    if (user?.role !== "Admin") return;
    const stranded = (leads || []).filter((l) => RETIRED_STAGE_MAP[l?.stage]);
    if (!stranded.length) return;
    stageMigrateRef.current = true;
    stranded.forEach((l) => {
      updateListingLead(
        l.id,
        { stage: RETIRED_STAGE_MAP[l.stage] },
        { skipFubPush: true },
      ).catch((e) => console.error("Retired-stage migration failed for", l.id, e));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, user?.id]);

  const isPrivileged =
    user?.role === "Admin" || user?.role === "Supervisor" || user?.role === "Accountant";


  const canManage = (lead) =>
    user?.role === "Admin" ||
    user?.role === "Supervisor" ||
    isOnLead(lead, user?.id, { ownerlessFallback: true });

  const visibleLeads = useMemo(
    () =>
      leads.filter((l) => {
        // Signed listings leave the active pipeline; "Show Won" brings them
        // back as a fully-converted tracker (dimmed, WON ✓ badge).
        if (l.status === "converted" && !showWon) return false;
        if (!showLost && l.status === "lost") return false;
        if (isPrivileged) {
          if (viewAgent === "all") return true;
          return isOnLead(l, viewAgent);
        }
        // Agents' own board: ownerless leads they entered stay visible to
        // them (they have no "All Agents" view to find them in).
        return isOnLead(l, user?.id, { ownerlessFallback: true });
      }),
    [leads, showLost, showWon, isPrivileged, user?.id, viewAgent],
  );

  // Fully-converted (signed) listings, for the tracker count in the toolbar.
  const wonCount = useMemo(
    () => (leads || []).filter((l) => l.status === "converted").length,
    [leads],
  );

  // Board search — filters cards by client name, property address, and agents.
  const searchTokens = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const matchesQuery = (l) => {
    if (searchTokens.length === 0) return true;
    const haystack = [
      l.contactName,
      l.propertyAddress,
      l.assignedAgentName,
      ...(Array.isArray(l.coAgents) ? l.coAgents.map((c) => c.name) : []),
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return searchTokens.every((t) => haystack.includes(t));
  };

  // The blend view gets the already-visible, already-searched leads.
  const shownLeads = useMemo(
    () => visibleLeads.filter(matchesQuery),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visibleLeads, query],
  );

  // Stage moves happen via the row dropdown (and the drawer) — same rules and
  // graduation hint the old drag-and-drop had.
  const moveStage = async (lead, newStage) => {
    if (!lead || !newStage || newStage === lead.stage) return;
    if (!canManage(lead)) {
      toast.error("You can only move your own leads");
      return;
    }
    dispatch({
      type: actionType.STORE_LISTING_LEADS,
      payload: leads.map((l) => (l.id === lead.id ? { ...l, stage: newStage } : l)),
    });
    const res = await updateListingLeadStage(lead.id, newStage);
    if (!res.success) toast.error("Failed to move lead");
    else if (newStage === GRADUATION_STAGE)
      toast.info('At Listing Appointment — once signed, open the card and tap "Sign the Listing" to move it to the Deals Board.');
  };

  // Middle column of a row: the property + money facts an agent scans for.
  const fmtMoney = (v) => {
    const n = Number(String(v ?? "").toString().replace(/[^0-9.]/g, ""));
    return n > 0 ? `$${n.toLocaleString("en-US")}` : "";
  };
  const secondaryLine = (l) =>
    [
      l.propertyAddress,
      fmtMoney(l.listPrice) ? `list ${fmtMoney(l.listPrice)}` : fmtMoney(l.estValue) ? `est ${fmtMoney(l.estValue)}` : "",
    ]
      .filter(Boolean)
      .join(" · ");

  const selectedLead = leads.find((l) => l.id === selectedId) || null;

  return (
    <div className="flex flex-col h-[calc(100vh-120px)]">
      <PageHeader
        title="Listing Pipeline"
        actions={
          <>
            {isPrivileged && (
              <select value={viewAgent} onChange={(e) => setViewAgent(e.target.value)}
                className="text-sm border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700">
                <option value="all">👁 Viewing: All Agents</option>
                {excludeTestAgents(employees).map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {[emp.firstname, emp.lastname].filter(Boolean).join(" ") || emp.email}
                  </option>
                ))}
              </select>
            )}
            <label className="text-sm text-slate-600 flex items-center gap-1.5">
              <input type="checkbox" checked={showLost} onChange={(e) => setShowLost(e.target.checked)} />
              Show Lost
            </label>
            <label className="text-sm text-slate-600 flex items-center gap-1.5">
              <input type="checkbox" checked={showWon} onChange={(e) => setShowWon(e.target.checked)} />
              Show Won
              {wonCount > 0 && (
                <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-100 rounded-full px-1.5 py-0.5">
                  {wonCount}
                </span>
              )}
            </label>
            <button onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-1.5 bg-[#1E293B] hover:opacity-90 text-white rounded-full px-4 py-2 text-sm font-semibold">
              <Plus size={16} /> Add Listing Lead
            </button>
          </>
        }
      />

      {/* Search — its own row beneath the header. */}
      <div className="px-3 pt-3 pb-1">
        <div className="relative w-full max-w-md">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search listings — name, address…"
            className="w-full text-sm border border-slate-200 rounded-lg pl-8 pr-7 py-1.5 bg-white text-slate-700 placeholder:text-slate-400"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              title="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto px-3 pb-3">
        <PipelineBlendView
          leads={shownLeads}
          stages={LISTING_STAGES}
          parkingStages={PARKING_STAGES}
          stageDot={(stage) => (
            <span
              className="rounded-full size-2.5 inline-block flex-shrink-0"
              style={{ backgroundColor: STAGE_COLORS[stage] }}
            />
          )}
          graduationStage={GRADUATION_STAGE}
          wonLabel="Won · Signed Listing"
          apptLabel="Listing appt"
          renderSecondary={secondaryLine}
          canManage={canManage}
          onOpen={(lead) => setSelectedId(lead.id)}
          onStageChange={moveStage}
        />
      </div>

      {addOpen && <NewListingLeadModal onClose={() => setAddOpen(false)} />}
      {selectedLead && (
        <ListingLeadDrawer
          lead={selectedLead}
          canManage={canManage(selectedLead)}
          onClose={() => setSelectedId(null)}
        />
      )}
    </div>
  );
}
